/**
 * Premium Hero Slider Custom Element
 * Handles auto-rotation, touch swiping, keyboard navigation, and transitions.
 */
class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.dots = [];
    this.currentIndex = 0;
    this.autoRotateTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-slider__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slider__dot'));
    this.prevBtn = this.querySelector('.hero-slider__arrow--prev');
    this.nextBtn = this.querySelector('.hero-slider__arrow--next');
    this.statusElement = this.querySelector('.hero-slider__status');
    
    if (this.slides.length === 0) return;

    this.autoRotate = this.dataset.autoRotate === 'true';
    this.rotationSpeed = parseInt(this.dataset.rotationSpeed, 10) || 5;
    this.transitionType = this.dataset.transition || 'fade';
    
    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.prefersReducedMotion) {
      this.autoRotate = false; // Disable auto-rotate if reduced motion is preferred
    }

    // Determine text direction for RTL support
    this.isRtl = document.dir === 'rtl' || (this.closest('[dir="rtl"]') !== null);

    this.bindEvents();
    this.setupTouchEvents();
    this.setupKeyboardNav();

    // Show initial slide without animation
    this.showSlide(0, { animate: false });

    if (this.autoRotate && this.slides.length > 1) {
      this.startAutoRotate();
      this.setupHoverPause();
    }
  }

  disconnectedCallback() {
    this.stopAutoRotate();
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prev();
        this.restartAutoRotate();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.next();
        this.restartAutoRotate();
      });
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.showSlide(index);
        this.restartAutoRotate();
      });
    });
  }

  setupTouchEvents() {
    const slidesContainer = this.querySelector('.hero-slider__slides');
    if (!slidesContainer) return;

    slidesContainer.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    slidesContainer.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const deltaX = this.touchEndX - this.touchStartX;

    if (Math.abs(deltaX) > swipeThreshold) {
      // If deltaX is negative, user swiped left.
      // In LTR, swiping left goes to next. In RTL, swiping left goes to prev.
      const swipedLeft = deltaX < 0;
      
      if (swipedLeft) {
        this.isRtl ? this.prev() : this.next();
      } else {
        this.isRtl ? this.next() : this.prev();
      }
      
      this.restartAutoRotate();
    }
  }

  setupKeyboardNav() {
    // Make the host element focusable if it isn't already via buttons
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.isRtl ? this.next() : this.prev();
        this.restartAutoRotate();
      } else if (e.key === 'ArrowRight') {
        this.isRtl ? this.prev() : this.next();
        this.restartAutoRotate();
      }
    });
  }

  setupHoverPause() {
    // Only setup hover pause on devices that support hover (not touch-only devices)
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => this.stopAutoRotate());
      this.addEventListener('mouseleave', () => this.startAutoRotate());
    }
  }

  showSlide(index, { animate = true } = {}) {
    // Wrap around for infinite loop
    let newIndex = index;
    if (newIndex < 0) {
      newIndex = this.slides.length - 1;
    } else if (newIndex >= this.slides.length) {
      newIndex = 0;
    }

    if (this.currentIndex === newIndex && animate) return;

    // Handle slide transition classes
    if (this.transitionType === 'slide' && animate && !this.prefersReducedMotion) {
      // Determine direction. 
      // If newIndex > currentIndex, it's next. Unless wrapping from last to 0.
      let isNext = newIndex > this.currentIndex;
      if (this.currentIndex === this.slides.length - 1 && newIndex === 0) isNext = true;
      if (this.currentIndex === 0 && newIndex === this.slides.length - 1) isNext = false;

      // Remove old transition classes
      this.slides.forEach(slide => {
        slide.classList.remove('is-prev', 'is-next');
      });

      // Prepare new slide position
      this.slides[newIndex].classList.add(isNext ? 'is-next' : 'is-prev');
      
      // Force a reflow so the browser registers the initial position before we transition to is-active
      void this.slides[newIndex].offsetWidth;
    }

    // Update active class
    this.slides.forEach((slide, i) => {
      if (i === newIndex) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });

    // Update dots
    this.dots.forEach((dot, i) => {
      if (i === newIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    // Update Live Region for screen readers
    if (this.statusElement) {
      this.statusElement.textContent = `Slide ${newIndex + 1} of ${this.slides.length}`;
    }

    this.currentIndex = newIndex;
  }

  next() {
    this.showSlide(this.currentIndex + 1);
  }

  prev() {
    this.showSlide(this.currentIndex - 1);
  }

  startAutoRotate() {
    if (!this.autoRotate || this.prefersReducedMotion || this.slides.length <= 1) return;
    this.stopAutoRotate();
    this.autoRotateTimer = setInterval(() => {
      this.next();
    }, this.rotationSpeed * 1000);
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  restartAutoRotate() {
    if (this.autoRotate) {
      this.startAutoRotate();
    }
  }
}

// Register the custom element
if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
